import { useMemo } from 'react';

import { getAuthHeaders } from '@/data/getAuthHeaders';
import { useHostProject, useOrganizationId } from '@/hooks/useSwr';

import { StudioClient } from '../api';
import { isHostedUrl } from '../utils';

const getAuthFetch =
  (tenantId: string | null, headers?: Record<string, string>): typeof fetch =>
  async (input, rawInit) => {
    const options: RequestInit = rawInit ?? {};
    const authHeaders = await getAuthHeaders();

    options.headers ??= {};
    if (options.headers != null && typeof options.headers === 'object') {
      options.headers = {
        'X-Tenant-Id': tenantId ?? '',
        'x-auth-scheme': 'langsmith',
        ...authHeaders,
        ...headers,
        ...options.headers,
      };
    }

    return await fetch(input, options);
  };

export type StudioClientWithFetch = [
  client: StudioClient,
  apiUrl: string,
  fetch: typeof fetch
];

export const useCreateStudioClient = ({
  projectId,
  baseUrl,
  headers,
}: {
  projectId?: string;
  baseUrl?: string;
  headers?: Record<string, string>;
}): StudioClientWithFetch | null => {
  const { data: project } = useHostProject(projectId ?? null);

  const apiUrl = project?.resource?.url ?? baseUrl;

  const tenantId = useOrganizationId();
  const client: StudioClientWithFetch | null = useMemo(() => {
    if (!apiUrl) return null;
    if (isHostedUrl(apiUrl)) {
      const authedFetch = getAuthFetch(tenantId, headers);

      return [
        new StudioClient({
          apiUrl,
          callerOptions: { fetch: authedFetch },
        }),
        apiUrl,
        authedFetch,
      ];
    }
    // If not hosted, we need to add the headers to the fetch function
    const authedFetch = getAuthFetch(tenantId, headers);
    return [
      new StudioClient({
        apiUrl,
        callerOptions: { fetch: authedFetch },
      }),
      apiUrl,
      authedFetch,
    ];
  }, [apiUrl, tenantId, headers]);

  return client;
};
